import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
    createDistributor,
    getDistributors,
    removeDistributor,
    updateDistributor,
} from '../../core/api/endpoints/distributors/distributorsApi';
import { AddDistributor } from '../../core/domainModels/distributors/AddDistributor';
import { Distributor } from '../../core/domainModels/distributors/Distributor';
import { UpdateDistributor } from '../../core/domainModels/distributors/UpdateDistributor';
import { TachoColumnState } from '../../core/domainModels/displayPreferences/tachoColumnState';
import { PersistConfig } from 'redux-persist';
import { fieldName } from '../../core/helpers/aggrid';
import autoMergeLevel1 from 'redux-persist/lib/stateReconciler/autoMergeLevel1';
import storage from 'redux-persist/lib/storage';

export interface DistributorsState {
    distributors: Array<Distributor>;
    fetchDistributorsPending: boolean;
    fetchDistributorsError: string;
    deleteDistributorPending: boolean;
    deleteDistributorError: string;
    addDistributorPending: boolean;
    addDistributorError: string;
    updateDistributorPending: boolean;
    updateDistributorError: string;
    displayPreferences: Array<TachoColumnState>;
    tableFilters: any;
}

const initialState: DistributorsState = {
    distributors: [],
    fetchDistributorsPending: false,
    fetchDistributorsError: '',
    deleteDistributorPending: false,
    deleteDistributorError: '',
    addDistributorPending: false,
    addDistributorError: '',
    updateDistributorPending: false,
    updateDistributorError: '',
    displayPreferences: [],
    tableFilters: {},
};

const name = 'distributors';

const fetchDistributorsThunk = createAsyncThunk(`${name}/fetch`, async (params, thunkAPI) => {
    try {
        return await getDistributors();
    } catch (error) {
        return thunkAPI.rejectWithValue(error);
    }
});

const deleteDistributorThunk = createAsyncThunk(`${name}/delete`, async (clientId: string, thunkAPI) => {
    try {
        return await removeDistributor(clientId);
    } catch (error) {
        return thunkAPI.rejectWithValue(error);
    }
});

const addDistributorThunk = createAsyncThunk(`${name}/add`, async (client: AddDistributor, thunkAPI) => {
    try {
        return await createDistributor(client);
    } catch (error: any) {
        return thunkAPI.rejectWithValue(error.message);
    }
});

const updateDistributorThunk = createAsyncThunk(`${name}/update`, async (user: UpdateDistributor, thunkAPI) => {
    try {
        return await updateDistributor(user);
    } catch (error) {
        return thunkAPI.rejectWithValue(error);
    }
});

const distributorsSlice = createSlice({
    name,
    initialState,
    reducers: {
        modifyDistributorsDisplayPreferences(state, action: PayloadAction<any>) {
            state.displayPreferences = action.payload;
        },
        modifyDistributorsTableFilters(state, action: PayloadAction<any>) {
            state.tableFilters = action.payload;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(fetchDistributorsThunk.pending, (state, action) => {
                state.fetchDistributorsPending = true;
                state.fetchDistributorsError = '';
            })
            .addCase(fetchDistributorsThunk.fulfilled, (state, action) => {
                state.fetchDistributorsError = '';
                state.distributors = action.payload;
                state.fetchDistributorsPending = false;
            })
            .addCase(fetchDistributorsThunk.rejected, (state, action) => {
                state.fetchDistributorsPending = false;
                state.fetchDistributorsError = action.payload as string;
            })
            .addCase(deleteDistributorThunk.pending, (state, action) => {
                state.deleteDistributorPending = true;
                state.deleteDistributorError = '';
            })
            .addCase(deleteDistributorThunk.fulfilled, (state, action) => {
                state.deleteDistributorError = '';
                state.distributors = state.distributors.filter(item => item.id !== action.payload);
                state.deleteDistributorPending = false;
            })
            .addCase(deleteDistributorThunk.rejected, (state, action) => {
                state.deleteDistributorPending = false;
                state.deleteDistributorError = action.payload as string;
            })
            .addCase(addDistributorThunk.pending, (state, action) => {
                state.addDistributorPending = true;
                state.addDistributorError = '';
            })
            .addCase(addDistributorThunk.fulfilled, (state, action) => {
                state.deleteDistributorError = '';
                state.addDistributorPending = false;
            })
            .addCase(addDistributorThunk.rejected, (state, action) => {
                state.addDistributorPending = false;
                state.addDistributorError = action.payload as string;
            })
            .addCase(updateDistributorThunk.pending, (state, action) => {
                state.updateDistributorPending = true;
                state.updateDistributorError = '';
            })
            .addCase(updateDistributorThunk.fulfilled, (state, action) => {
                const updatedUserIndex = state.distributors.findIndex(c => c.id === action.payload.distributorId);
                if (updatedUserIndex !== -1) {
                    state.distributors[updatedUserIndex] = { ...state.distributors[updatedUserIndex], ...action.payload };
                }
                state.updateDistributorPending = false;
            })
            .addCase(updateDistributorThunk.rejected, (state, action) => {
                state.updateDistributorPending = false;
                state.updateDistributorError = action.payload as string;
            });
    },
});
export const distributorsReducerConfig: PersistConfig<DistributorsState> = {
    key: name,
    storage: storage,
    stateReconciler: autoMergeLevel1,
    whitelist: [fieldName<DistributorsState>('displayPreferences'), fieldName<DistributorsState>('tableFilters')],
};
const { reducer, actions } = distributorsSlice;
const { modifyDistributorsDisplayPreferences, modifyDistributorsTableFilters } = actions;
export { reducer as distributorsReducer };
export {
    fetchDistributorsThunk,
    deleteDistributorThunk,
    addDistributorThunk,
    updateDistributorThunk,
    modifyDistributorsDisplayPreferences,
    modifyDistributorsTableFilters,
};
